/**
 * Capture a test idea into the local backlog. Shared by the experiments
 * decision table footer and the board's Ideas lane.
 */

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { addExperimentIdea, type ExperimentIdea } from "@/lib/utils/experimentIdeas"

export function CaptureIdeaDialog({
  open,
  onOpenChange,
  propertyId,
  onCaptured,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  propertyId: string
  onCaptured?: (idea: ExperimentIdea) => void
}) {
  const [title, setTitle] = useState("")
  const [hypothesis, setHypothesis] = useState("")
  const [pageUrl, setPageUrl] = useState("")

  const handleCapture = () => {
    if (!title.trim()) return
    const entry = addExperimentIdea({
      propertyId,
      title: title.trim(),
      hypothesis: hypothesis.trim() || "If we change this, then conversion improves because…",
      pageUrl: pageUrl.trim() || undefined,
      source: "manual",
      impact: 7,
      confidence: 5,
      ease: 6,
    })
    onOpenChange(false)
    setTitle("")
    setHypothesis("")
    setPageUrl("")
    onCaptured?.(entry)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm font-medium">Capture an idea</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Stronger CTA on pricing"
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Hypothesis</Label>
            <Textarea
              value={hypothesis}
              onChange={(e) => setHypothesis(e.target.value)}
              placeholder="If we … then … because …"
              className="min-h-[72px] text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Page (optional)</Label>
            <Input
              value={pageUrl}
              onChange={(e) => setPageUrl(e.target.value)}
              placeholder="/pricing"
              className="h-9 font-mono text-xs"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCapture} disabled={!title.trim()}>
            Save idea
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
